// @ts-strict-ignore
import { Component, Input, OnInit } from '@angular/core';
import { ValidatorFactory } from '@shared/validator.factory';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ProjectModel } from '@project/model/project.model';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CustomValidators } from '@shared/custom-validators';
import { ProjectRequestProcessor } from '@project/components/request/processor/project-request.processor';
import { AlertHelper } from '@shared/alert/alert.helper';
import { AlertButton } from '@shared/alert/alertButton';
import { ArticleVO } from '@article/model/vo/article.vo';
import { StringUtils } from '@shared/util/string-utils';
import { UserSettings } from '@user/model/user-settings';
import { MultipleQuoteRequestData } from './multiple-quote-request-data';
import { ProjectHeadVO, ProjectVO } from '@project/model/vo/project.vo';
import { FinalizationData } from './processor/finalization-data';
import { SystemConfigurationService } from '@shared/system-configuration/system-configuration-loading.service';
import { IsProjectType } from '../../model/is-project-type';
import { ProjectComponentPresenter } from '../project-component.presenter';

@Component({
  standalone: false,
  templateUrl: './project-request.component.html',
  selector: 'app-project-request',
})
export class ProjectRequestComponent implements OnInit {
  public readonly ADDITIONAL_EMAILS_COUNT: number = 5;

  private matchAddressPattern: string;
  private isAssemblyOfProjectImmediatelyPossible: boolean = false;
  private email: string;
  private showUnavailableArticles: boolean = false;
  private articlesUnderStockLevel: ArticleVO[];
  public orderDirectly: boolean;

  @Input()
  public multipleQuoteRequestData: MultipleQuoteRequestData;
  @Input()
  public totalDemand: number;
  @Input()
  public batchSize: number = 1;
  @Input()
  public deliveryDate: Date;
  @Input()
  public remarks: string = '';
  @Input()
  public sapProjectNumber: string;
  public form: FormGroup;
  public showRequiredFields = false;
  public startDate: Date;
  public endDate: Date;
  public bsValue: any;
  public isRequestSingleQuoteMode: boolean = true;

  constructor(
    private formBuilder: FormBuilder,
    private _bsModalRef: BsModalRef,
    private userSettings: UserSettings,
    private projectModel: ProjectModel,
    private localeResourceManager: LocaleResourceManager,
    private systemConfigurationService: SystemConfigurationService,
    private alertHelper: AlertHelper,
    private projectRequestProcessor: ProjectRequestProcessor,
    private projectComponentPresenter: ProjectComponentPresenter,
    private validatorFactory: ValidatorFactory,
  ) {}

  public ngOnInit(): void {
    this.matchAddressPattern = `(.*${this.systemConfigurationService.currentConfiguration.internalEmailPostfix}[.][a-zA-z]+)$`;
    if (this.multipleQuoteRequestData != null) {
      this.isRequestSingleQuoteMode = false;
    }
    this.orderDirectly = false;
    this.startDate = new Date();
    this.startDate.setDate(this.startDate.getDate() + this.systemConfigurationService.currentConfiguration.requestDaysInFuture);
    this.endDate = new Date();
    this.endDate.setFullYear(this.endDate.getFullYear() + 1);
    this.form = this.formBuilder.group(this.getFormModel());
    this.form.controls['totalDemand'].setValue(this.totalDemand || '');
    this.form.controls['batchSize'].setValue(this.batchSize || '1');
    if (this.deliveryDate != null) {
      this.form.controls['deliveryDate'].setValue(this.deliveryDate);
    } else {
      this.form.controls['deliveryDate'].setValue(this.startDate);
    }
    this.fillPreviousAdditionalEmails();
    for (let i = 0; i < this.ADDITIONAL_EMAILS_COUNT; i++) {
      this.form.controls['email' + i].valueChanges.subscribe(() => this.form.controls['email' + i].markAsPristine());
      this.form.controls['emailCheckbox' + i].valueChanges.subscribe(() => this.form.controls['emailCheckbox' + i].markAsPristine());
    }
    this.form.controls['remarks'].setValue(this.remarks || '');
    this.isAssemblyOfProjectImmediatelyPossible = true;
  }

  public markControlAsDirty(control: string): void {
    this.form.controls[control].markAsDirty();
  }

  public async close(successful: boolean): Promise<void> {
    if (successful) {
      const finalizationData: FinalizationData = new FinalizationData({
        totalDemand: this.form.value.totalDemand,
        batchSize: this.form.value.batchSize,
        deliveryDate: this.form.value.deliveryDate,
        sapProjectNumber: this.form.value.sapProjectNumber,
        remarks: this.form.value.remarks,
        emailAddress: this.email,
        orderDirectly: this.orderDirectly,
        additionalEmails: this.getAdditionalEmails(),
      });

      if (this.isRequestSingleQuoteMode) {
        await this.projectRequestProcessor.requestFinalizeProject(finalizationData);
      } else {
        finalizationData.projectsForMultipleRequest = this.multipleQuoteRequestData.projects;
        await this.projectRequestProcessor.requestFinalizeProject(finalizationData, false);
      }
    }
    this._bsModalRef.hide();
  }

  public getHeader(): string {
    if (this.isRequestSingleQuoteMode) {
      return this.localeResourceManager.getValueByKey('projectRequestPopup.header', [this.projectModel.currentProject.name]);
    } else {
      return this.localeResourceManager.getValueByKey('projectRequestPopup.header.multiple_quote', [
        this.multipleQuoteRequestData.projectName,
        this.multipleQuoteRequestData.projects.length,
      ]);
    }
  }

  public getEmailByProjectType(): string {
    let projectToCheck: ProjectHeadVO;

    if (this.isRequestSingleQuoteMode) {
      projectToCheck = this.projectModel.currentProject;
    } else {
      const projectsHaveSameType: boolean = this.checkProjectsForProjectTypeIdentity();
      if (projectsHaveSameType) {
        projectToCheck = this.multipleQuoteRequestData.projects[0];
      } else {
        return this.systemConfigurationService.currentConfiguration.requestEmail;
      }
    }
    if (IsProjectType.mcsGds(projectToCheck)) {
      return this.systemConfigurationService.currentConfiguration.requestMcsGdsEmail;
    } else if (IsProjectType.markingSystem(projectToCheck)) {
      return this.systemConfigurationService.currentConfiguration.requestMarkingcardsEmail;
    }

    return this.systemConfigurationService.currentConfiguration.requestEmail;
  }

  private checkProjectsForProjectTypeIdentity(): boolean {
    if (this.multipleQuoteRequestData.projects.length) {
      const projectTypes: string[] = this.multipleQuoteRequestData.projects.map((project: ProjectVO) => project.projectType);
      const uniqueProjectTypes: string[] = Array.from(new Set(projectTypes));
      return uniqueProjectTypes.length === 1 ? true : false;
    }
  }

  public showMcsGdsInfo(): boolean {
    if (this.isRequestSingleQuoteMode) {
      return IsProjectType.gds(this.projectModel.currentProjectType)
        ? true
        : IsProjectType.mcsGds(this.projectModel.currentProjectType);
    } else {
      return false;
    }
  }

  public checkControlValidity(controlName: string, useDirty: boolean = false): boolean {
    const control = this.form.controls[controlName];
    if (control.value === '') {
      useDirty ? control.markAsPristine() : control.markAsUntouched();
    }
    return (control.invalid && (useDirty ? control.dirty : control.touched)) || (control.invalid && this.showRequiredFields && !useDirty);
  }

  public async submit(): Promise<void> {
    for (let i = 0; i < this.ADDITIONAL_EMAILS_COUNT; i++) {
      this.markControlAsDirty('email' + i);
      this.markControlAsDirty('emailCheckbox' + i);
    }
    if (!this.form.valid) {
      this.showRequiredFields = true;
      return;
    }
    if (!this.isAssemblyOfProjectImmediatelyPossible) {
      const proceed = await this.projectComponentPresenter
        .openProjectRequestUnavailableArticlesPopup(this.showUnavailableArticles, this.articlesUnderStockLevel);
      if (proceed) {
        await this.continueProjectRequest();
      }
    } else {
      await this.continueProjectRequest();
    }
  }

  private async continueProjectRequest(): Promise<void> {
    this.email = this.getEmailByProjectType();

    const answer = await this.alertHelper
      .showAlert(
        'projectRequestPopup.title.confirm_sending_request',
        'projectRequestPopup.message.confirm_sending_request',
        null,
        [AlertButton.OK, AlertButton.CANCEL],
        "info"
      );

    if (answer === AlertButton.OK) {
      await this.close(true);
    }
  }

  private getFormModel(): any {
    const formModel = {
      totalDemand: [
        '',
        Validators.compose([
          CustomValidators.trimRequired,
          this.validatorFactory.number({min: 0, max: 99999999, precision: 0}),
        ]),
      ],
      batchSize: [
        '',
        Validators.compose([
          CustomValidators.trimRequired,
          this.validatorFactory.number({min: 0, max: 99999999, precision: 0}),
        ]),
      ],
      deliveryDate: ['', null],
      remarks: ['', CustomValidators.minMaxLength(0, 1000, '', this.localeResourceManager.getValueByKey('projectRequestPopup.error_tooLongError'))],
      sapProjectNumber: [
        '',
        Validators.compose([
          this.validatorFactory.number({min: 0, max: 99999999, precision: 0}),
        ]),
      ],
      email: ['', CustomValidators.emailValidator(false)],
      emailConfirm: ['', Validators.compose([CustomValidators.emailValidator(false), CustomValidators.matchEmailsValidator('email')])],
      acceptTerms: ['', null],
    };
    for (let i = 0; i < this.ADDITIONAL_EMAILS_COUNT; i++) {
      formModel['email' + i] = ['', CustomValidators.emailValidator(true)];
      formModel['emailCheckbox' + i] = [false, CustomValidators.requiredCheckboxForRequest('email' + i, this.matchAddressPattern)];
    }
    return formModel;
  }

  private fillPreviousAdditionalEmails() {
    const lastAdditionalRequestEmails = this.userSettings.lastAdditionalRequestEmails;
    if (lastAdditionalRequestEmails) {
      for (let i = 0; i < lastAdditionalRequestEmails.length; i++) {
        this.form.controls['email' + i].setValue(lastAdditionalRequestEmails[i]);
        this.form.controls['emailCheckbox' + i].setValue(true);
      }
    }
  }

  private getAdditionalEmails(): string[] {
    const additionalEmails: string[] = [];
    for (let i = 0; i < this.ADDITIONAL_EMAILS_COUNT; i++) {
      const value = this.form.controls['email' + i].value;
      if (StringUtils.isNotEmpty(value)) {
        additionalEmails.push(value);
      }
    }
    return additionalEmails;
  }

  public showCheckbox(inputControl: AbstractControl, checkboxControl: AbstractControl): boolean {
    if (!inputControl || !checkboxControl) {
      return null;
    }
    const inputValue: string = inputControl.value.trim();
    return inputValue && !inputControl.invalid && !this.isWagoEmail(inputValue);
  }

  public resetCheckbox(checkboxControl: AbstractControl): void {
    if (!checkboxControl) {
      return;
    }
    checkboxControl.setValue(false);
  }

  public isWagoEmail(value: string): boolean {
    return !!value.toLowerCase().match(this.matchAddressPattern);
  }

  public preventValidationOnClose(): void {
    this.form.markAsUntouched();
    this.form.markAsPristine();
  }
}
